/**
 * When Outlook OAuth is opened from My Profile in a popup, Microsoft redirects
 * back to the configured Angular OutlookRedirectURL with ?code=. Hand the result
 * to the opener and close so Link/Delink can update on My Profile.
 *
 * Also writes to localStorage so My Profile can finish even when window.opener
 * is null (common after Microsoft login due to Cross-Origin-Opener-Policy).
 */
export const OUTLOOK_OAUTH_STORAGE_KEY = "intimepro-outlook-oauth-result";
export const OUTLOOK_OAUTH_POPUP_NAME = "intimepro-outlook-oauth";
export const KEK_OAUTH_POPUP_NAME = "intimepro-keka-oauth";
export type OutlookOAuthRelayPayload = {
    source: "intimepro-outlook";
    success: boolean;
    code?: string;
    message?: string;
    provider: number;
    at: number;
};

export function relayOutlookOAuthResultToOpener(code?: string | null, error?: string | null, errorDescription?: string | null): boolean {
    if (!code && !error) {
        return false;
    }

    const payload: OutlookOAuthRelayPayload = {
        source: "intimepro-outlook",
        success: !error,
        code: code || undefined,
        message: errorDescription || error || undefined,
        provider: 1,
        at: Date.now(),
    };

    let opener: Window | null = null;
    try {
        opener = window.opener && window.opener !== window ? window.opener : null;
    } catch {
        opener = null;
    }

    const isNamedOutlookPopup = (() => {
        try {
            return window.name === OUTLOOK_OAUTH_POPUP_NAME;
        } catch {
            return false;
        }
    })();

    // Popup flows (with or without opener after COOP): hand off to My Profile.
    if (opener || isNamedOutlookPopup) {
        try {
            localStorage.setItem(OUTLOOK_OAUTH_STORAGE_KEY, JSON.stringify(payload));
        } catch {
            // Ignore quota / private mode failures.
        }

        if (opener) {
            try {
                opener.postMessage(payload, window.location.origin);
            } catch {
                // localStorage fallback remains for the opener.
            }
        }

        try {
            window.close();
        } catch {
            // Opener / storage still received the result.
        }

        return true;
    }

    // Full-page legacy redirect (Time Tracking checkbox): let that page exchange the code.
    return false;
}

export function readOutlookOAuthResultFromStorage(maxAgeMs = 5 * 60 * 1000): OutlookOAuthRelayPayload | null {
    try {
        const raw = localStorage.getItem(OUTLOOK_OAUTH_STORAGE_KEY);
        if (!raw) {
            return null;
        }

        const payload = JSON.parse(raw) as OutlookOAuthRelayPayload;
        if (!payload || payload.source !== "intimepro-outlook") {
            return null;
        }

        if (!payload.at || Date.now() - payload.at > maxAgeMs) {
            localStorage.removeItem(OUTLOOK_OAUTH_STORAGE_KEY);
            return null;
        }

        return payload;
    } catch {
        return null;
    }
}

export function clearOutlookOAuthResultFromStorage(): void {
    try {
        localStorage.removeItem(OUTLOOK_OAUTH_STORAGE_KEY);
    } catch {
        // ignore
    }
}
